import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Box, CircularProgress, IconButton, Tooltip, Typography, keyframes } from "@mui/material";

const recordPulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.25; }
  100% { opacity: 1; }
`;

const equalize = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50%      { transform: scaleY(1); }
`;

/** Decorative live "waveform" — a row of bars bouncing at staggered phases. It
 * signals "recording" without sampling real amplitude (the genuine waveform is
 * computed from the finished audio on playback). */
function LiveEqualizer(): JSX.Element {
  const bars = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  return (
    <Box
      aria-hidden
      sx={{ display: "flex", alignItems: "center", gap: 0.4, height: 22, flex: 1, minWidth: 0 }}
    >
      {bars.map((i) => (
        <Box
          key={i}
          sx={{
            width: 3,
            height: "100%",
            borderRadius: 2,
            bgcolor: "chat.accent",
            transformOrigin: "center",
            animation: `${equalize} 0.9s ease-in-out ${(i % 6) * 0.12}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

/** Format ms as M:SS (a voice note is short; no hours). */
export function formatRecordTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * The WhatsApp-style recording bar shown in place of the composer input while a
 * voice message is being recorded: a trash/cancel button, a pulsing red dot +
 * live equalizer + elapsed timer, and a send button. Pure presentation — the
 * recording lifecycle lives in `useVoiceRecorder`; the composer wires the
 * callbacks.
 */
export function VoiceRecorderBar({
  elapsedMs,
  uploading = false,
  onCancel,
  onSend,
}: {
  elapsedMs: number;
  uploading?: boolean;
  onCancel: () => void;
  onSend: () => void;
}): JSX.Element {
  return (
    <Box
      data-testid="chat-voice-bar"
      sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1 }}
    >
      <Tooltip title="Cancel">
        <span style={{ display: "inline-flex" }}>
          <IconButton
            aria-label="Cancel voice message"
            data-testid="chat-voice-cancel"
            onClick={onCancel}
            disabled={uploading}
            sx={{ color: "error.main", mb: 0.25 }}
          >
            <DeleteRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "background.paper",
          borderRadius: 4,
          px: 1.75,
          py: 0.75,
          boxShadow: 1,
        }}
      >
        <FiberManualRecordRoundedIcon
          sx={{ color: "error.main", fontSize: 14, animation: `${recordPulse} 1.4s infinite` }}
        />
        <Typography
          data-testid="chat-voice-timer"
          sx={{ fontSize: 14, fontVariantNumeric: "tabular-nums", color: "text.primary", minWidth: 38 }}
        >
          {formatRecordTime(elapsedMs)}
        </Typography>
        <LiveEqualizer />
      </Box>

      <IconButton
        onClick={onSend}
        disabled={uploading}
        aria-label="Send voice message"
        data-testid="chat-voice-send"
        sx={{
          bgcolor: "chat.accent",
          color: "#fff",
          width: 44,
          height: 44,
          flexShrink: 0,
          "&:hover": { bgcolor: "chat.accent", filter: "brightness(0.92)" },
          "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
        }}
      >
        {uploading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : <SendRoundedIcon />}
      </IconButton>
    </Box>
  );
}
